import React from 'react';

export default function DTOsExplanation() {
    const [selectedSection, setSelectedSection] = React.useState('definition');

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                    <h2 className="text-2xl font-bold mb-4">Data Transfer Objects (DTOs)</h2>
                    
                    <div className="mb-4 flex space-x-2">
                        {['definition', 'structure', 'examples', 'implementation'].map(section => (
                            <button
                                key={section}
                                onClick={() => setSelectedSection(section)}
                                className={`px-4 py-2 rounded ${
                                    selectedSection === section
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-100'
                                }`}
                            >
                                {section.charAt(0).toUpperCase() + section.slice(1)}
                            </button>
                        ))}
                    </div>

                    {selectedSection === 'definition' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Rôle des DTOs</h3>
                            <ul className="space-y-2 list-disc list-inside text-gray-700">
                                <li>Transfert de données entre couches</li>
                                <li>Isolation du modèle de domaine</li>
                                <li>Adaptation des données pour l'API</li>
                                <li>Validation des données d'entrée</li>
                                <li>Documentation des contrats d'API</li>
                            </ul>
                        </div>
                    )}

                    {selectedSection === 'structure' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Structure Type</h3>
                            <pre className="bg-gray-50 p-4 rounded text-sm">
{`// Request DTO
@Schema(description = "Demande de création de paiement")
public record CreatePaymentRequest(
    @NotNull(message = "Le montant est obligatoire")
    @Positive(message = "Le montant doit être positif")
    @Schema(description = "Montant du paiement", example = "100.00")
    BigDecimal amount,

    @NotNull(message = "La devise est obligatoire")
    @Schema(description = "Code devise ISO", example = "EUR")
    String currency,

    @NotBlank(message = "L'IBAN débiteur est obligatoire")
    @Pattern(regexp = "^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$",
            message = "Format IBAN invalide")
    @Schema(description = "IBAN du compte débiteur")
    String debtorIban,

    @NotBlank(message = "L'IBAN créditeur est obligatoire")
    @Pattern(regexp = "^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$",
            message = "Format IBAN invalide")
    @Schema(description = "IBAN du compte créditeur")
    String creditorIban,

    @Schema(description = "Référence du paiement")
    String reference,

    @Future(message = "La date d'exécution doit être future")
    @Schema(description = "Date d'exécution souhaitée")
    LocalDate executionDate
) {}

// Response DTO
@Schema(description = "Réponse de création de paiement")
public record PaymentResponse(
    @Schema(description = "Identifiant unique")
    UUID id,

    @Schema(description = "Montant")
    MoneyDTO amount,

    @Schema(description = "Statut du paiement")
    PaymentStatus status,

    @Schema(description = "Référence")
    String reference,

    @Schema(description = "Date de création")
    LocalDateTime createdAt
) {
    // Méthodes utilitaires si nécessaire
}

// DTO imbriqué
@Schema(description = "Représentation d'un montant monétaire")
public record MoneyDTO(
    @Schema(description = "Valeur")
    BigDecimal value,

    @Schema(description = "Code devise")
    String currency
) {}`}
                            </pre>
                        </div>
                    )}

                    {selectedSection === 'examples' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Exemples de DTOs</h3>
                            
                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">DTOs pour Messages SWIFT</h4>
                                <pre className="text-xs mt-2">
{`// Request DTO pour MT103
@Schema(description = "Création d'un message MT103")
public record MT103Request(
    @NotNull
    @Schema(description = "Détails de la transaction")
    TransactionDetailsDTO transaction,

    @NotNull
    @Schema(description = "Informations sur l'ordonnant")
    OrderingPartyDTO orderingParty,

    @NotNull
    @Schema(description = "Informations sur le bénéficiaire")
    BeneficiaryDTO beneficiary,

    @Schema(description = "Instructions spéciales")
    List<String> instructions
) {}

// DTO imbriqué pour les détails de transaction
@Schema(description = "Détails de la transaction")
public record TransactionDetailsDTO(
    @NotNull
    @Schema(description = "Référence de transaction")
    String reference,

    @NotNull
    @Schema(description = "Montant et devise")
    MoneyDTO amount,

    @Schema(description = "Date de valeur")
    @Future
    LocalDate valueDate
) {}

// Response DTO
@Schema(description = "Réponse MT103")
public record MT103Response(
    @Schema(description = "Identifiant du message")
    String messageId,

    @Schema(description = "Statut")
    MessageStatus status,

    @Schema(description = "Référence SWIFT")
    String swiftReference,

    @Schema(description = "Horodatage")
    LocalDateTime timestamp
) {}`}
                                </pre>
                            </div>

                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">DTOs pour Mandats</h4>
                                <pre className="text-xs mt-2">
{`// Request DTO
@Schema(description = "Création d'un mandat")
public record CreateMandateRequest(
    @NotNull
    @Schema(description = "Identifiant du créancier")
    String creditorId,

    @NotNull
    @Schema(description = "Information du débiteur")
    DebtorDTO debtor,

    @Schema(description = "Type de mandat")
    MandateType type,

    @Schema(description = "Scheme du mandat")
    String scheme,

    @NotNull
    @Schema(description = "Date de signature")
    LocalDate signatureDate
) {}

// Response DTO
@Schema(description = "Réponse mandat")
public record MandateResponse(
    @Schema(description = "Référence du mandat")
    String reference,

    @Schema(description = "Statut")
    MandateStatus status,

    @Schema(description = "Date de début")
    LocalDate startDate,

    @Schema(description = "Date de fin")
    LocalDate endDate,

    @Schema(description = "Historique des modifications")
    List<MandateAmendmentDTO> amendments
) {}`}
                                </pre>
                            </div>
                        </div>
                    )}

                    {selectedSection === 'implementation' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Mappers</h3>
                            
                            <div className="space-y-2">
                                <h4 className="font-medium">1. Mapper avec MapStruct</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`@Mapper(componentModel = "spring")
public interface PaymentMapper {
    @Mapping(target = "id", ignore = true)
    @Mapping(source = "amount", target = "amount.value")
    @Mapping(source = "currency", target = "amount.currency")
    Payment toEntity(CreatePaymentRequest request);

    @Mapping(source = "amount.value", target = "amount")
    @Mapping(source = "amount.currency", target = "currency")
    PaymentResponse toDto(Payment payment);

    default MoneyDTO map(Amount amount) {
        return new MoneyDTO(
            amount.getValue(),
            amount.getCurrency().getCurrencyCode()
        );
    }
}`}
                                </pre>
                            </div>

                            <div className="space-y-2">
                                <h4 className="font-medium">2. Validation Personnalisée</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`@Validated
public class CustomValidator {
    @Constraint(validatedBy = IbanValidator.class)
    @Target({ElementType.FIELD})
    @Retention(RetentionPolicy.RUNTIME)
    public @interface ValidIban {
        String message() default "IBAN invalide";
        Class<?>[] groups() default {};
        Class<? extends Payload>[] payload() default {};
    }

    public class IbanValidator 
        implements ConstraintValidator<ValidIban, String> {
        
        @Override
        public boolean isValid(String iban, 
            ConstraintValidatorContext context) {
            return validateIban(iban);
        }
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-lg p-4 shadow">
                    <h3 className="text-xl font-bold mb-4">Bonnes Pratiques</h3>
                    
                    <div className="space-y-4">
                        <div className="border-l-4 border-blue-500 pl-4">
                            <h4 className="font-semibold">1. Immutabilité</h4>
                            <p className="text-sm text-gray-600">
                                Utiliser des records ou des classes immutables
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                record, final class with final fields
                            </code>
                        </div>

                        <div className="border-l-4 border-green-500 pl-4">
                            <h4 className="font-semibold">2. Validation</h4>
                            <p className="text-sm text-gray-600">
                                Validation complète avec Bean Validation
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                @NotNull, @Valid, Custom Validators
                            </code>
                        </div>

                        <div className="border-l-4 border-purple-500 pl-4">
                            <h4 className="font-semibold">3. Documentation</h4>
                            <p className="text-sm text-gray-600">
                                Documentation claire avec OpenAPI
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                @Schema, Examples, Descriptions
                            </code>
                        </div>

                        <div className="border-l-4 border-yellow-500 pl-4">
                            <h4 className="font-semibold">4. Mapping</h4>
                            <p className="text-sm text-gray-600">
                                Mappers explicites et testables
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                MapStruct, Custom Mappers
                            </code>
                        </div>

                        <div className="border-l-4 border-red-500 pl-4">
                            <h4 className="font-semibold">5. Versioning</h4>
                            <p className="text-sm text-gray-600">
                                Gestion des versions d'API
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                Separate DTOs per version
                            </code>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}